<script lang="ts" setup>
import type { NavLink } from '#core/types'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@forgd/ui-kit/collapsible'
import { useBodyScrollLock } from '@forgd/ui-kit/sheet'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'

const props = defineProps<{
  items?: NavLink[]
  label?: string
  activeLabel?: string
  lockScroll?: boolean
}>()

const route = useRoute()
const open = ref(false)
const trigger = useTemplateRef<InstanceType<typeof CollapsibleTrigger>>('trigger')

const currentLabel = computed(() => {
  if (props.activeLabel) {
    return props.activeLabel
  }
  const match = props.items?.find(item => item.to === route.path)
  return match?.label ?? props.items?.[0]?.label ?? ''
})

watch(() => route.fullPath, () => {
  open.value = false
})

// the nav is md:hidden, so a menu left open while widening past md would keep the body locked
const isDesktop = useBreakpoints(breakpointsTailwind).greaterOrEqual('md')
watch(isDesktop, (value) => {
  if (value) {
    open.value = false
  }
})

// shared stack with CorePageHeaderMobile, so closing one menu does not unlock the body under the other
const isBodyScrollLocked = useBodyScrollLock()
watchEffect(() => {
  isBodyScrollLocked.value = props.lockScroll && open.value
})

// only links close the panel, so disclosure controls inside it (e.g. an accordion) stay usable
function closeFromPanel(event: MouseEvent) {
  if (!(event.target instanceof Element) || !event.target.closest('a')) {
    return
  }
  open.value = false
  trigger.value?.$el?.focus()
}
</script>

<template>
  <Collapsible
    v-model:open="open"
    data-el="CoreMobileNav"
    class="md:hidden sticky top-14 z-30 w-full border-b border-default bg-neutral-200"
  >
    <CollapsibleTrigger ref="trigger" class="w-full flex items-center justify-between gap-2 px-4 py-3 text-left">
      <span class="min-w-0 flex items-baseline gap-1.5">
        <span v-if="label" class="text-xs text-secondary shrink-0">{{ label }}:</span>
        <span class="truncate text-sm font-semibold text-primal">{{ currentLabel }}</span>
      </span>
      <ChevronDownIcon class="size-4 shrink-0 text-secondary motion-safe:transition-transform motion-safe:duration-300" :class="{ 'rotate-180': open }" />
    </CollapsibleTrigger>
    <div class="absolute inset-x-0 top-full">
      <CollapsibleContent>
        <div class="border-b border-default bg-neutral-200 px-2 py-2 max-h-[calc(100dvh-104px)] overflow-y-auto" @click="closeFromPanel">
          <slot />
        </div>
      </CollapsibleContent>
    </div>
  </Collapsible>
</template>
